<script setup lang="ts">
import EntryAttachments from '@/core/attachments/EntryAttachments.vue';
import { EntryAttachmentType } from 'common/enum/entry.attachment.enum';

import type { Components } from '@/api/generated/api';
import EntryAnnotationField from '@/core/dialogs/tabbed-detail-dialog/EntryAnnotationField.vue';
import DetailRelationActionRow from '@/core/dialogs/tabbed-detail-dialog/DetailRelationActionRow.vue';
import CompetenceSelection from '@/core/selects/CompetenceSelection.vue';
import StudentSelection from '@/core/selects/StudentSelection.vue';
import type { ISelectedCompetence, TStudentLike } from '@/interfaces';
import { NOTE_TEXT_MAX_LENGTH } from 'common/constants/note.constants';
import { useTemplateRef } from 'vue';

defineProps<{
  allEligibleStudentsSelected: boolean;
  allSchoolSubjects: Components.Schemas.SchoolSubjectDto[];
  competenceDomains: Components.Schemas.CompetenceDomainDto[];
  competenceSelectionDisabled: boolean;
  description: string;
  entryId: number;
  materials: Components.Schemas.ExternalMaterialLinkDto[];
  disabled: boolean;
  eligibleStudents: TStudentLike[];
  relationsSaving: boolean;
  schoolSubjectId: number | null;
  selectedCompetences: ISelectedCompetence[];
  selectedStudents: TStudentLike[];
  showStudentSelection: boolean;
}>();

const emit = defineEmits<{
  'update:materials': [ids: number[]];
  'add:competence': [competenceId: number];
  'commit:description': [value: string];
  'remove:competence': [competenceId: number];
  'select:all-students': [];
  'update:competences': [competenceIds: number[]];
  'update:description': [value: string];
  'update:students': [students: TStudentLike[]];
}>();

const studentSelectionRef = useTemplateRef<InstanceType<typeof StudentSelection>>('studentSelectionRef');
const competenceSelectionRef = useTemplateRef<InstanceType<typeof CompetenceSelection>>('competenceSelectionRef');

const hideOverlays = (): void => {
  studentSelectionRef.value?.hide();
  competenceSelectionRef.value?.hide();
};

defineExpose({ hideOverlays });
</script>

<template>
  <div class="flex flex-col gap-md p-content max-md:p-lg">
    <StudentSelection
      v-if="showStudentSelection"
      ref="studentSelectionRef"
      class="max-w-full self-start"
      :all-eligible-students-selected="allEligibleStudentsSelected"
      :disable-click-outside="relationsSaving"
      :disabled="relationsSaving"
      commit-on-close
      :eligible-students="eligibleStudents"
      mobile-drawer
      :read-only="disabled"
      :selected-students="selectedStudents"
      @select:all-eligible-students="emit('select:all-students')"
      @update:students="emit('update:students', $event)"
    />

    <EntryAnnotationField
      :disabled="disabled"
      :label="$t('performanceAssessmentEntry.notes')"
      :maxlength="NOTE_TEXT_MAX_LENGTH"
      name="performance-assessment-entry-detail-description"
      :value="description"
      @update:value="emit('update:description', $event)"
      @commit="emit('commit:description', $event)"
    />

    <EntryAttachments
      :key="entryId"
      :entry-type="EntryAttachmentType.PerformanceAssessment"
      :entry-id="entryId"
      :materials="materials"
      :readonly="disabled"
      :can-edit="relationsSaving ? false : undefined"
      @update:materials="emit('update:materials', $event)"
    />

    <section v-if="selectedCompetences.length" class="flex flex-col gap-sm">
      <h2 class="flex min-h-button items-center py-sm text-body font-semibold leading-[1.7] text-secondary">
        {{ $t('performanceAssessmentEntry.detailDialog.linkedCompetences') }}
      </h2>
      <CompetenceSelection
        ref="competenceSelectionRef"
        :all-school-subjects="allSchoolSubjects"
        :competence-domains="competenceDomains"
        commit-on-close
        :disable-click-outside="relationsSaving"
        :disabled="relationsSaving || competenceSelectionDisabled"
        mobile-drawer
        :read-only="disabled"
        :school-subject-id="schoolSubjectId"
        :selected-competences="selectedCompetences"
        surface="secondary"
        @add:competence="emit('add:competence', $event)"
        @remove:competence="emit('remove:competence', $event)"
        @update:competences="emit('update:competences', $event)"
      />
    </section>

    <DetailRelationActionRow v-else-if="!disabled">
      <CompetenceSelection
        ref="competenceSelectionRef"
        :all-school-subjects="allSchoolSubjects"
        :competence-domains="competenceDomains"
        commit-on-close
        :disable-click-outside="relationsSaving"
        :disabled="relationsSaving || competenceSelectionDisabled"
        inline-empty
        mobile-drawer
        :school-subject-id="schoolSubjectId"
        :selected-competences="selectedCompetences"
        surface="secondary"
        @add:competence="emit('add:competence', $event)"
        @remove:competence="emit('remove:competence', $event)"
        @update:competences="emit('update:competences', $event)"
      />
    </DetailRelationActionRow>
  </div>
</template>
